import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL        = environment.internalChatSocketURL;
const EVENT_MSG_UPDATE  = 'internalChatMessageUpdate';
const EVENT_CONV_UPDATE = 'internalChatConversationUpdate';
const EVENT_MARK_READ   = 'markAsRead';
const EVENT_MSG_RECEIPT  = 'internalChatMessageReceiptUpdate';
const EVENT_GROUP_UPDATE = 'internalChatGroupUpdate';
const EVENT_MSG_MUTATION = 'internalChatMessageMutation';

@Injectable({
  providedIn: 'root'
})
export class InternalChatService implements OnDestroy {

  private chatApi = environment.internalChatApiBaseUrl; // https://chat.crizac.com/api/v1

  private socket: Socket | null = null;
  private messageUpdate$ = new Subject<any>();
  private convUpdate$    = new Subject<any>();
  private receiptUpdate$ = new Subject<any>();
  private groupUpdate$   = new Subject<any>();
  private mutationUpdate$ = new Subject<any>();
  private activeConvId   = '';

  constructor(private http: HttpClient) {}

  ngOnDestroy() {
    this.disconnectSocket();
  }

  // ── Socket ────────────────────────────────────────────────────────────────

  get isConnected(): boolean { return !!this.socket?.connected; }

  initSocket(token: string, userId: string): void {
    if (!token || !userId) return;

    // If socket already connected — just ensure listeners are attached
    if (this.socket?.connected) {
      this.attachListeners();
      return;
    }

    // If socket exists but disconnected (broken connection) — destroy and recreate
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.socket = io(SOCKET_URL, {
      query:      { token, userId },
      transports: ['websocket'],
      reconnection:         true,
      reconnectionAttempts: Infinity,
      reconnectionDelay:    1000,
      reconnectionDelayMax: 5000,
    });

    this.attachListeners();
  }

  private attachListeners(): void {
    if (!this.socket) return;

    this.socket.off(EVENT_MSG_UPDATE);
    this.socket.off(EVENT_CONV_UPDATE);
    this.socket.off(EVENT_MSG_RECEIPT);
    this.socket.off(EVENT_GROUP_UPDATE);
    this.socket.off(EVENT_MSG_MUTATION);
    this.socket.off('connect');
    this.socket.off('connect_error');

    this.socket.on(EVENT_MSG_UPDATE,   (msg: any)  => this.messageUpdate$.next(msg));
    this.socket.on(EVENT_CONV_UPDATE,  (conv: any) => this.convUpdate$.next(conv));
    this.socket.on(EVENT_MSG_RECEIPT,  (data: any) => this.receiptUpdate$.next(data));
    this.socket.on(EVENT_GROUP_UPDATE, (data: any) => this.groupUpdate$.next(data));
    this.socket.on(EVENT_MSG_MUTATION, (data: any) => this.mutationUpdate$.next(data));

    this.socket.on('connect', () => {
      if (this.activeConvId) this.joinConversation(this.activeConvId);
    });

    this.socket.on('connect_error', (err) => {
      console.warn('[InternalChat] Socket connection error:', err.message);
    });
  }

  joinConversation(conversationId: string): void {
    this.activeConvId = conversationId;
    if (!this.socket?.connected) return;
    this.socket.io.opts.query = { channelId: conversationId } as any;
  }

  markAsRead(conversationId: string, receiverUserId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit(EVENT_MARK_READ, { conversationId, receiverUserId });
  }

  disconnectSocket(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  onMessageUpdate(): Observable<any>      { return this.messageUpdate$.asObservable(); }
  onConversationUpdate(): Observable<any> { return this.convUpdate$.asObservable(); }
  onReceiptUpdate(): Observable<any>      { return this.receiptUpdate$.asObservable(); }
  onGroupUpdate(): Observable<any>        { return this.groupUpdate$.asObservable(); }
  onMessageMutation(): Observable<any>    { return this.mutationUpdate$.asObservable(); }

  // ── HTTP: Conversations ───────────────────────────────────────────────────

  getConversations(receiverUserId: string, skip = 0, limit = 20): Observable<any> {
    return this.http.get(`${this.chatApi}/conversations?skip=${skip}&limit=${limit}`);
  }

  getMessages(conversationId: string, options: { filter?: 'media' | 'attachments' | 'starred'; search?: string; page?: number; limit?: number } = {}): Observable<any> {
    const params: Record<string, string> = {};
    if (options.filter)         params['filter'] = options.filter;
    if (options.search?.trim()) params['search'] = options.search.trim();
    if (options.page)           params['page']   = options.page.toString();
    if (options.limit)          params['limit']  = options.limit.toString();
    const query = Object.keys(params).length ? `?${new URLSearchParams(params)}` : '';
    return this.http.get(`${this.chatApi}/conversations/${conversationId}/messages${query}`);
  }

  getUserInfo(userId: string): Observable<any> {
    return this.http.get(`${this.chatApi}/conversations/user/${userId}/info`);
  }

  // ── HTTP: Messaging ───────────────────────────────────────────────────────

  sendMessage(senderId: string, receiverId: string, message: string, referenceMsgId?: string, messageType = 'text', conversationId?: string, mentions?: { userId: string; name: string }[]): Observable<any> {
    const payload: any = { senderId, receiverId, messageType, message };
    if (referenceMsgId)  payload.referenceMsgId = referenceMsgId;
    if (conversationId)  payload.conversationId  = conversationId;
    if (mentions?.length) payload.mentions       = mentions;
    return this.http.post(`${this.chatApi}/whatsapp/send/agent`, payload);
  }

  sendMessageWithAttachment(senderId: string, receiverId: string, files: File[], captions?: (string | null)[], referenceMsgId?: string, conversationId?: string, mentions?: { userId: string; name: string }[]): Observable<any> {
    const formData    = new FormData();
    const messageType = this.getMessageType(files);
    formData.append('senderId',    senderId);
    formData.append('receiverId',  receiverId);
    formData.append('messageType', messageType);
    files.forEach(file => formData.append('files', file, file.name));
    if (messageType === 'media_group') {
      // Backend expects one caption per file, at the same index as its thumbnail (null where there is none).
      const perFile = files.map((_, i) => captions?.[i]?.trim() || null);
      if (perFile.some(c => c)) formData.append('captions', JSON.stringify(perFile));
    } else {
      const text = captions?.[0]?.trim();
      if (text) formData.append('message', text);
    }
    if (referenceMsgId)    formData.append('referenceMsgId', referenceMsgId);
    if (conversationId)    formData.append('conversationId', conversationId);
    if (mentions?.length)  formData.append('mentions',       JSON.stringify(mentions));
    return this.http.post(`${this.chatApi}/whatsapp/send/agent`, formData);
  }

  updateMessage(messageId: string, senderId: string, newMessage: string): Observable<any> {
    return this.http.patch(`${this.chatApi}/conversations/messages/${messageId}`, {
      senderId,
      message: newMessage,
    });
  }

  deleteMessage(messageId: string, senderId: string): Observable<any> {
    return this.http.delete(`${this.chatApi}/conversations/messages/${messageId}`, {
      body: { senderId },
    });
  }

  deleteMediaItems(messageIds: string[]): Observable<any> {
    return this.http.delete(`${this.chatApi}/conversations/messages`, {
      body: { messageIds },
    });
  }

  toggleStarMessage(messageId: string): Observable<any> {
    return this.http.patch(`${this.chatApi}/conversations/messages/${messageId}/important`, {});
  }

  // ── HTTP: Users ───────────────────────────────────────────────────────────

  getNewChatUsers(skip: number, limit: number, username: string = ''): Observable<any> {
    const params: Record<string, string> = {
      skip: skip.toString(),
      limit: limit.toString(),
    };
    if (username.trim()) params['username'] = username.trim();
    const query = new URLSearchParams(params).toString();
    return this.http.get(`${environment.internalChatApiBaseUrl}/users/userextension?${query}`);
  }

  // ── HTTP: Groups ──────────────────────────────────────────────────────────

  getGroupInfo(conversationId: string): Observable<any> {
    return this.http.get(`${this.chatApi}/conversations/${conversationId}/group/info`);
  }

  addGroupMembers(conversationId: string, users: { userId: string; name: string; role: number }[]): Observable<any> {
    return this.http.post(`${this.chatApi}/conversations/${conversationId}/members`, { users });
  }

  removeGroupMember(conversationId: string, memberId: string): Observable<any> {
    return this.http.delete(`${this.chatApi}/conversations/${conversationId}/members/${memberId}`);
  }

  getMessageReceipts(messageId: string): Observable<any> {
    return this.http.get(`${this.chatApi}/conversations/messages/${messageId}/receipts`);
  }

  updateMemberRole(conversationId: string, memberUserId: string, role: number): Observable<any> {
    return this.http.patch(`${this.chatApi}/conversations/${conversationId}/members/${memberUserId}/role`, { role });
  }

  updateGroup(conversationId: string, payload: { groupName?: string; file?: File | null }): Observable<any> {
    const formData = new FormData();
    if (payload.groupName) formData.append('groupName', payload.groupName);
    if (payload.file)      formData.append('file', payload.file);
    return this.http.patch(`${this.chatApi}/conversations/${conversationId}/group`, formData);
  }

  createGroup(payload: { name: string; users: { userId: string; name: string; role: number }[]; file?: File | null }): Observable<any> {
    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('users', JSON.stringify(payload.users));
    if (payload.file) {
      formData.append('file', payload.file);
    }
    return this.http.post(`${this.chatApi}/conversations/group`, formData);
  }

  private getMessageType(files: File[]): string {
    if (files.length > 1) return 'media_group';
    const t = files[0].type;
    if (t.startsWith('image/')) return 'image';
    if (t.startsWith('video/')) return 'video';
    if (t.startsWith('audio/')) return 'audio';
    return 'document';
  }

  // ── Avatar utilities ───────────────────────────────────────────────────
  getAvatarColor(name: string | undefined | null): string {
    const colors = [
      '#dff2e0', '#fde8d8', '#d8eaf8', '#f3d8f5',
      '#fdf3d8', '#d8f5f0', '#fdd8e0', '#e0d8fd',
      '#f5f0d8', '#d8f0fd',
    ];
    if (!name) return colors[0];
    const hash = Array.from(name).reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return colors[hash % colors.length];
  }

  getGroupIconColor(name: string | undefined | null): string {
    const colors = [
      '#2e7d32', '#e65100', '#1565c0', '#6a1b9a',
      '#f57f17', '#00695c', '#b71c1c', '#4527a0',
      '#827717', '#01579b',
    ];
    if (!name) return colors[0];
    const hash = Array.from(name).reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return colors[hash % colors.length];
  }
}